/// <reference path="../../css.d.ts" />
"use client";

// Source : UIArc `registry/components/dialog` (MIT © 2026 Elia Kuratli), commit 7238f98196b147a8d50ce467abcbad310d4d8414.
// Adaptations UBM (lot U2) : moteur commun `./engine` (Radix non modal + modalité suspendable du crochet partagé,
// engagement d'hôte, pont d'Échap) à la place des primitives Radix nues ; libellé du bouton fermer paramétrable ;
// presets de mouvement exportés pour les façades. Rendu, styles et mouvements : ceux de la source.
import { useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import type { HTMLMotionProps, Transition } from "motion/react";
import { X } from "lucide-react";
import { motionTokens } from "../../lib/motion-tokens";
import { Root, Trigger, Close, Portal, Overlay, Content, Title, Description, useDialogEngine } from "./engine";
import type { RootProps, ContentProps } from "./engine";
import "../../styles/foundation.css";
import styles from "./dialog.module.css";

/** Mirrors the open state so the content can stay mounted while it animates out, and retarget mid-flight if it is reopened or closed early. */
export function Dialog(props: RootProps) {
  return <Root {...props} />;
}

export const DialogTrigger = Trigger;
export const DialogClose = Close;

export interface DialogContentProps extends Omit<ContentProps, "title"> {
  title: string;
  description?: string;
  children: ReactNode;
  /** Nom accessible du bouton fermer. Défaut `"Fermer le dialogue"` (adaptation française). */
  closeLabel?: string;
}

/**
 * Classes de la peau source (voile, carte, en-tête, titre, description, corps, fermer) : la façade product-ui les porte, peau unique.
 * `surface` (lot E) : la peinture de `content` sans sa géométrie de viewport, pour une surface hébergée ailleurs.
 */
export const dialogClassNames = {
  overlay: styles.overlay as string,
  content: styles.content as string,
  surface: styles.surface as string,
  header: styles.header as string,
  title: styles.title as string,
  description: styles.description as string,
  body: styles.body as string,
  close: styles.close as string,
  swap: styles.swap as string,
};

const fade: Transition = { duration: motionTokens.duration.instant };
const leave: Transition = { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] };

/** When the title or description changes while open, the new copy rises in and the old copy leaves upward. */
export function DialogSwapText({ text, className }: { text: string; className?: string }) {
  const reduced = useReducedMotion();
  return <AnimatePresence mode="popLayout" initial={false}>
    <motion.span key={text} className={className ?? styles.swap} initial={reduced ? false : { opacity: 0, y: "0.3em", filter: `blur(${motionTokens.blur.soft}px)` }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: "-0.3em", filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] } }} transition={{ duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}>{text}</motion.span>
  </AnimatePresence>;
}

/** Mouvement source du voile : fondu à l'entrée, sortie plus courte et plus discrète. */
export function dialogOverlayMotion(reduced: boolean | null): Pick<HTMLMotionProps<"div">, "initial" | "animate" | "exit" | "transition"> {
  return { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0, transition: reduced ? fade : leave }, transition: reduced ? fade : { duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] } };
}

/** Mouvement source de la carte : monte de 8px et grandit sur un ressort ; la sortie repart d'où l'entrée en est. */
export function dialogContentMotion(reduced: boolean | null): Pick<HTMLMotionProps<"div">, "initial" | "animate" | "exit" | "transition"> {
  return { initial: reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: .96 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: reduced ? { opacity: 0, transition: fade } : { opacity: 0, y: 4, scale: .98, transition: leave }, transition: reduced ? fade : { default: motionTokens.spring.smooth, opacity: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.enter] } } };
}

export function DialogContent({ title, description, children, className, closeLabel = "Fermer le dialogue", onPointerDownOutside, ...props }: DialogContentProps) {
  const open = useDialogEngine()?.open ?? null;
  const reduced = useReducedMotion();
  // When the open state last changed. Radix waits for the click before treating a press as outside, and a press on the trigger
  // while the dialog leaves reopens it first, so that press must not close it again.
  const change = useRef({ open, at: 0 });
  useLayoutEffect(() => { change.current = { open, at: performance.now() }; }, [open]);
  const pressOutside: DialogContentProps["onPointerDownOutside"] = event => {
    onPointerDownOutside?.(event);
    if (open !== null && (!change.current.open || event.detail.originalEvent.timeStamp < change.current.at)) event.preventDefault();
  };
  const classes = [styles.content, className].filter(Boolean).join(" ");
  const inner = <>
    <div className={styles.header}><div><Title className={styles.title}><DialogSwapText text={title}/></Title>{description ? <Description className={styles.description}><DialogSwapText text={description}/></Description> : null}</div><Close className={styles.close} aria-label={closeLabel}><X size={16} strokeWidth={1.75} aria-hidden="true"/></Close></div>
    <div className={styles.body}>{children}</div>
  </>;
  const describedBy = description ? {} : { "aria-describedby": undefined };
  // Under a bare Radix root the open state is unknown here, so CSS keyframes keyed off data-state animate the layers instead.
  if (open === null) return <Portal>
    <Overlay className={`${styles.overlay} ${styles.keyframes}`}/>
    <Content {...describedBy} {...props} onPointerDownOutside={pressOutside} className={`${classes} ${styles.keyframes}`}>{inner}</Content>
  </Portal>;
  // The overlay fades while the dialog rises 8px and scales up on a spring. Closing is shorter and quieter, and starts from wherever the entrance is.
  return <AnimatePresence>
    {open && <Portal key="dialog" forceMount>
      <Overlay asChild forceMount><motion.div className={styles.overlay} {...dialogOverlayMotion(reduced)}/></Overlay>
      <Content {...describedBy} {...props} onPointerDownOutside={pressOutside} asChild forceMount>
        <motion.div className={classes} {...dialogContentMotion(reduced)}>{inner}</motion.div>
      </Content>
    </Portal>}
  </AnimatePresence>;
}
